import apiClient from "./apiClient";
import {buildRelationIncludesUrl} from "./detail";
import {EventDto, ManageGoogleCalendarEventDto} from "../core/Event";
import {buildUrlFromSearch, Search} from "./search";
import {DeclareEventPresenceDto, EventGuestPresenceDecisionDto, EventGuestPresenceFilters} from "../core/EventGuest";
import {DeclareEventPowerDelegationDto} from "../core/EventPowerDelegation";
import {AttendanceSheetGenerationOptions, EventAttendanceSheetGeneration} from "../core/EventAttendanceSheetGeneration";
import {
    EventAttendanceSheetSubmission,
    UploadAttendanceSheetSubmissionDocument,
    UploadAttendanceSheetSubmissionResult,
} from "../core/EventAttendanceSheetSubmission";
import {ResolveGoogleMeetAttendancesDto} from "../core/GoogleMeetAttendance";
import type {SendEventMailDto} from "../core/SendEventMail";

export async function fetchMeetingRoomAvailability({startAt, endAt, eventId, selectedMeetingRoomId}: {
    startAt: string;
    endAt: string;
    eventId?: string;
    selectedMeetingRoomId?: string;
}, signal?: AbortSignal): Promise<boolean | null> {
    const parameters = new URLSearchParams({startAt, endAt});

    if (eventId) {
        parameters.set('eventId', eventId);
    }
    if (selectedMeetingRoomId) {
        parameters.set('selectedMeetingRoomId', selectedMeetingRoomId);
    }

    const response = await apiClient(`/api/event/list_available_meeting_rooms?${parameters}`, {signal});
    const result = await response.json();

    if (selectedMeetingRoomId && typeof result?.selectedMeetingRoomAvailable !== 'boolean') {
        throw new Error('Missing selected room availability');
    }
    
    return selectedMeetingRoomId ? result.selectedMeetingRoomAvailable : null;
}

export async function fetchEvents(search: Search) : Promise<Response> {
    return await apiClient(
        buildUrlFromSearch('/api/event', search),
        {method: 'GET'}
    );
}


export async function fetchEvent(id: string, relationIncludes: Array<string> = []) : Promise<Response> {
    return await apiClient(
        buildRelationIncludesUrl(`/api/event/${id}`, relationIncludes),
        {method: 'GET'}
    );
}

export async function addEvent(event: EventDto) : Promise<Response> {
    return await apiClient('/api/event', {
        method: 'POST',
        body: JSON.stringify(event)
    });
}

export async function updateEvent(id: string, event: EventDto) : Promise<Response> {
    return await apiClient(`/api/event/${id}`, {
        method: 'PUT',
        body: JSON.stringify(event)
    });
}

export async function deleteEvent(id: string) : Promise<Response> {
    return await apiClient(`/api/event/${id}`, {
        method: 'DELETE'
    });
}

export async function cancelEvent(id: string) : Promise<Response> {
    return await apiClient(`/api/event/${id}/cancel`, {
        method: 'POST'
    });
}

export async function addContactAdditionalGuest(eventId: string, contactId: string) : Promise<Response> {
    return await apiClient(`/api/event/${eventId}/add_contact_additional_guest`, {
        method: 'POST',
        body: JSON.stringify({
            contactId
        })
    });
}

export async function addGroup(eventId: string, groupId: string, isPrincipal: boolean = false) : Promise<Response> {
    return await apiClient(`/api/event/${eventId}/add_group`, {
        method: 'POST',
        body: JSON.stringify({
            groupId,
            isPrincipal
        })
    });
}

export async function deleteContactAdditionalGuestFromEvent(eventId: string, contactAdditionalGuestId: string) : Promise<Response> {
    return await apiClient(`/api/event/${eventId}/remove_contact_additional_guest`, {
        method: 'POST',
        body: JSON.stringify({
            contactId: contactAdditionalGuestId
        })
    });
}

export async function deleteGroupFromEvent(eventId: string, groupId: string) : Promise<Response> {
    return await apiClient(`/api/event/${eventId}/remove_group`, {
        method: 'POST',
        body: JSON.stringify({
            groupId
        })
    });
}

export function declareEventPresence(declareEventPresenceDto: DeclareEventPresenceDto) : Promise<Response> {
    return apiClient(`/api/event/declare_presence`, {
        method: 'POST',
        body: JSON.stringify(declareEventPresenceDto)
    });
}

export async function fetchEventGuest(token: string, relationIncludes: Array<string> = []) : Promise<Response> {
    return await apiClient(
        buildRelationIncludesUrl(`/api/event/event_guest/${token}`, relationIncludes),
        {method: 'GET'}
    );
}

export async function fetchPowerDelegation(token: string, relationIncludes: Array<string> = []) : Promise<Response> {
    return await apiClient(
        buildRelationIncludesUrl(`/api/event/power_delegation/${token}`, relationIncludes),
        {method: 'GET'}
    );
}

export function declarePowerDelegation(declareEventPowerDelegationDto: DeclareEventPowerDelegationDto) : Promise<Response> {
    return apiClient(`/api/event/declare_power_delegation`, {
        method: 'POST',
        body: JSON.stringify(declareEventPowerDelegationDto)
    });
}

export async function sendEventInvitations(eventId: string) : Promise<Response> {
    return await apiClient(`/api/event/${eventId}/send_invitations`, {
        method: 'POST'
    });
}

export async function remindUnansweredEventGuests(eventId: string): Promise<Response> {
    return await apiClient(`/api/event/${eventId}/remind_unanswered_guests`, {
        method: 'POST'
    });
}

export function sendMailToAllEventGuests(eventId: string, mail: SendEventMailDto): Promise<Response> {
    return apiClient(`/api/event/${eventId}/send_mail_to_all_guests`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(mail),
    });
}

export async function sendEventInvitation(eventGuestId: string) : Promise<Response> {
    return await apiClient(`/api/event/event_guest/${eventGuestId}/send_invitation`, {
        method: 'POST'
    });
}

export async function sendEventInformation(eventGuestId: string) : Promise<Response> {
    return await apiClient(`/api/event/event_guest/${eventGuestId}/send_information`, {
        method: 'POST'
    });
}

export async function sendEventInformationToPresentGuests(eventId: string) : Promise<Response> {
    return await apiClient(`/api/event/${eventId}/send_information`, {
        method: 'POST'
    });
}

export async function createGoogleCalendarEvent(eventId: string, manageGoogleCalendarEventDto?: ManageGoogleCalendarEventDto) : Promise<Response> {
    return await apiClient(`/api/event/${eventId}/create_google_calendar_event`, {
        method: 'POST',
        body: JSON.stringify(manageGoogleCalendarEventDto)
    });
}

export async function updateGoogleCalendarEvent(eventId: string, manageGoogleCalendarEventDto?: ManageGoogleCalendarEventDto) : Promise<Response> {
    return await apiClient(`/api/event/${eventId}/update_google_calendar_event`, {
        method: 'POST',
        body: JSON.stringify(manageGoogleCalendarEventDto)
    });
}

export async function createGoogleMeet(eventId: string) : Promise<Response> {
    return await apiClient(`/api/event/${eventId}/create_google_meet`, {
        method: 'POST'
    });
}

export async function removeGoogleMeet(eventId: string) : Promise<Response> {
    return await apiClient(`/api/event/${eventId}/remove_google_meet`, {
        method: 'POST'
    });
}

export async function fetchEventGuests(search: Search) : Promise<Response> {
    return await apiClient(
        buildUrlFromSearch('/api/event_guest', search),
        {method: 'GET'}
    );
}

export function fetchEventReceivedPowerDelegations(eventId: string): Promise<Response> {
    return apiClient(`/api/event/event_power_delegations/${eventId}`, {
        method: 'GET'
    });
}

export async function fetchEventPresenceGuests(eventId: string, page: number, itemsPerPage: number, filters: EventGuestPresenceFilters) : Promise<Response> {
    const query = new URLSearchParams({
        page: page.toString(),
        itemsPerPage: itemsPerPage.toString(),
    });
    if (filters.verifiedPresenceType !== null) {
        query.set('verifiedPresenceType', filters.verifiedPresenceType);
    }
    if (filters.ambiguity !== null) {
        query.set('ambiguity', filters.ambiguity ? 'true' : 'false');
    }

    return await apiClient(`/api/event/${eventId}/presence-guests?${query.toString()}`, {
        method: 'GET',
    });
}

export async function decideEventGuestPresence(eventId: string, eventGuestId: string, decision: EventGuestPresenceDecisionDto) : Promise<Response> {
    return await apiClient(`/api/event/${eventId}/presence-guests/${eventGuestId}/decisions`, {
        method: 'POST',
        body: JSON.stringify(decision)
    });
}

export function fetchEventGuestPaperMarks(eventId: string, eventGuestId: string): Promise<Response> {
    return apiClient(`/api/event/${eventId}/presence-guests/${eventGuestId}/paper-marks`);
}

export function fetchExtraEventAttendees(search: Search) : Promise<Response> {
    return apiClient(
        buildUrlFromSearch('/api/extra_event_attendee', search), {
        method: 'GET'
    });
}

export function closeEvent(eventId: string) : Promise<Response> {
    return apiClient(`/api/event/${eventId}/close`, {
        method: 'POST'
    });
}

export function fetchEventAttendanceSheetGenerations(eventId: string): Promise<Response> {
    return apiClient(`/api/event/${eventId}/attendance-sheet-generations`);
}

export async function createEventAttendanceSheetGeneration(
    eventId: string,
    idempotencyKey: string,
    options: AttendanceSheetGenerationOptions,
): Promise<EventAttendanceSheetGeneration> {
    const response = await apiClient(
        `/api/event/${eventId}/attendance-sheet-generations`,
        {
            method: "POST",
            headers: {
                "Idempotency-Key": idempotencyKey,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(options),
        },
    );

    return response.json();
}

export function fetchEventAttendanceSheetSubmissions(eventId: string): Promise<Response> {
    return apiClient(`/api/event/${eventId}/attendance-sheet-submissions?orders[uploadedAt]=DESC`);
}

export async function uploadEventAttendanceSheetSubmission(
    eventId: string,
    documents: UploadAttendanceSheetSubmissionDocument[],
): Promise<UploadAttendanceSheetSubmissionResult> {
    const response = await apiClient(
        `/api/event/${eventId}/attendance-sheet-submissions`,
        {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({documents}),
        },
    );
    return response.json();
}

export async function analyzeEventAttendanceSheetSubmission(
    eventId: string,
    submissionId: string,
): Promise<{analysisStatus: EventAttendanceSheetSubmission["analysisStatus"]}> {
    const response = await apiClient(`/api/event/${eventId}/attendance-sheet-submissions/${submissionId}/analyze`, {
        method: "POST",
    });

    return response.json();
}

export function synchronizeGoogleMeetAttendance(eventId: string): Promise<Response> {
    return apiClient(`/api/event/${eventId}/synchronize_google_meet_attendance`, {
        method: 'POST'
    });
}

export function fetchGoogleMeetParticipantAttendances(search: Search): Promise<Response> {
    return apiClient(
        buildUrlFromSearch('/api/google_meet_participant_attendance', search), {
        method: 'GET'
    });
}

export function resolveGoogleMeetAttendances(eventId: string, resolveGoogleMeetAttendancesDto: ResolveGoogleMeetAttendancesDto): Promise<Response> {
    return apiClient(`/api/event/${eventId}/resolve_google_meet_attendances`, {
        method: 'POST',
        body: JSON.stringify(resolveGoogleMeetAttendancesDto)
    });
}
